import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import MarketActionPanel from "components/markets/MarketActionPanel";
import { statusLabel, statusTone } from "components/markets/marketActions";
import usePortalAudience, { createdByLabel, usePartnerNames } from "components/markets/usePortalAudience";
import { apiGet } from "lib/apiClient";
import { APPROVAL_ACTION_META, ApprovalHistoryList, useApprovalHistory } from "components/backoffice/Backoffice";

function HistoryCard({ state, title, description }) {
  return (
    <Card extra="p-5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-base font-semibold text-navy-700 dark:text-white">{title}</p>
        {!state.loading && <StatusBadge label={String(state.total)} tone="gray" />}
      </div>
      <p className="mb-3 mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      <ApprovalHistoryList state={state} />
    </Card>
  );
}

function SubmissionRow({ market, latest }) {
  const meta = latest ? APPROVAL_ACTION_META[latest.action] : null;
  const sentBack = market.status === "DRAFT" && latest?.action === "REJECTED";
  return (
    <div className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/portal/markets/${market.id}`} className="text-sm font-semibold text-navy-700 hover:underline dark:text-white">
            {market.question}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusBadge label={statusLabel(market.status)} tone={statusTone(market.status)} />
            {meta && <StatusBadge label={meta.label} tone={meta.tone} />}
            {latest && <span className="text-xs text-gray-500">{new Date(latest.at).toLocaleString()}</span>}
          </div>
        </div>
        <Link
          to={`/portal/markets/${market.id}`}
          className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-semibold text-navy-700 dark:border-white/10 dark:text-white"
        >
          {sentBack ? "Fix and resubmit" : "Open market page"}
        </Link>
      </div>
      {sentBack && latest.notes && (
        <p className="mt-2 rounded-xl bg-red-50 p-3 text-xs text-red-600 dark:bg-red-400/10 dark:text-red-300">
          <span className="font-semibold">Reviewer feedback: </span>
          {latest.notes}
        </p>
      )}
    </div>
  );
}

function PartnerApprovals() {
  const drafts = useMarketsByStatus("DRAFT", true, 0);
  const pending = useMarketsByStatus("PENDING_APPROVAL", true, 0);
  const history = useApprovalHistory({ pageSize: 200 });
  const latestByMarket = new Map();
  for (const item of history.items) {
    if (["SUBMITTED", "APPROVED", "REJECTED"].includes(item.action) && !latestByMarket.has(item.marketId)) latestByMarket.set(item.marketId, item);
  }
  const submissions = [...pending.items, ...drafts.items];
  const loading = drafts.loading || pending.loading;

  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Approvals</p>
        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
          Where each of your market submissions stands. Your tenant's reviewers approve markets before they open; if a market is sent back, their
          feedback shows here so you can fix it and resubmit.
        </p>
      </Card>
      <Card extra="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-base font-semibold text-navy-700 dark:text-white">Your submissions</p>
          {!loading && <StatusBadge label={String(submissions.length)} tone={pending.items.length > 0 ? "amber" : "gray"} />}
        </div>
        {(drafts.error || pending.error) && <p className="mt-3 text-sm text-red-500">{drafts.error || pending.error}</p>}
        {loading ? (
          <p className="mt-3 text-sm text-gray-500">Loading…</p>
        ) : submissions.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">
            Nothing waiting. <Link to="/portal/markets/new" className="font-semibold text-brand-500 hover:underline">Create a market</Link> to submit one.
          </p>
        ) : (
          <div className="mt-2 divide-y divide-gray-100 dark:divide-white/5">
            {submissions.map((m) => (
              <SubmissionRow key={m.id} market={m} latest={latestByMarket.get(m.id)} />
            ))}
          </div>
        )}
      </Card>
      <HistoryCard state={history} title="Approval history" description="Every decision on your markets: who acted, when, and the notes they left." />
    </div>
  );
}

function useMarketsByStatus(status, enabled, reloadKey) {
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    apiGet(`/portal/markets?status=${status}&page=1&pageSize=50`)
      .then((res) => {
        if (!cancelled) setState({ items: res.markets || [], total: res.total || 0, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ items: [], total: 0, loading: false, error: err.message || "Could not load markets." });
      });
    return () => {
      cancelled = true;
    };
  }, [status, enabled, reloadKey]);
  return state;
}

function MarketRow({ market, audience, partnerNames, onChanged }) {
  const [decision, setDecision] = useState(undefined);
  const needsDecision = market.status === "RESULT_PROPOSED" && audience.can("market.settle") && audience.tenantId;

  useEffect(() => {
    if (!needsDecision) return undefined;
    let cancelled = false;
    apiGet(`/admin/tenants/${audience.tenantId}/markets/${market.id}/decision`)
      .then((res) => {
        if (!cancelled) setDecision(res.decision);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [needsDecision, audience.tenantId, market.id]);

  const who = createdByLabel(market, audience, partnerNames);
  return (
    <div className="py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/portal/markets/${market.id}`} className="text-sm font-semibold text-navy-700 hover:underline dark:text-white">
            {market.question}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusBadge label={statusLabel(market.status)} tone={statusTone(market.status)} />
            <StatusBadge label={who.label} tone={who.tone} />
            <span className="text-xs text-gray-500">
              Opens {new Date(market.opensAt).toLocaleDateString()}, closes {new Date(market.closesAt).toLocaleDateString()}
            </span>
          </div>
        </div>
        <Link
          to={`/portal/markets/${market.id}`}
          className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-semibold text-navy-700 dark:border-white/10 dark:text-white"
        >
          Open market page
        </Link>
      </div>
      <div className="mt-3">
        <MarketActionPanel market={market} audience={audience} decision={decision} onChanged={onChanged} />
      </div>
    </div>
  );
}

function Section({ title, description, state, audience, partnerNames, onChanged, empty }) {
  return (
    <Card extra="p-5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-base font-semibold text-navy-700 dark:text-white">{title}</p>
        {!state.loading && <StatusBadge label={String(state.total)} tone={state.total > 0 ? "amber" : "gray"} />}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      {state.error && <p className="mt-3 text-sm text-red-500">{state.error}</p>}
      {state.loading ? (
        <p className="mt-3 text-sm text-gray-500">Loading…</p>
      ) : state.items.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="mt-2 divide-y divide-gray-100 dark:divide-white/5">
          {state.items.map((m) => (
            <MarketRow key={m.id} market={m} audience={audience} partnerNames={partnerNames} onChanged={onChanged} />
          ))}
        </div>
      )}
    </Card>
  );
}

export default function ApprovalsPage() {
  const audience = usePortalAudience();
  const partnerNames = usePartnerNames(audience.isTenant);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const enabled = audience.isTenant;
  const pendingMarkets = useMarketsByStatus("PENDING_APPROVAL", enabled, reloadKey);
  const proposedResults = useMarketsByStatus("RESULT_PROPOSED", enabled, reloadKey);
  const [pendingSeries, setPendingSeries] = useState({ items: [], loading: true, error: null });
  const history = useApprovalHistory({ pageSize: 50, reloadKey });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    apiGet("/portal/market-series")
      .then((res) => {
        if (!cancelled) setPendingSeries({ items: (res.series || []).filter((s) => s.status === "PENDING_APPROVAL"), loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setPendingSeries({ items: [], loading: false, error: err.message || "Could not load recurring series." });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey]);

  if (audience.loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (audience.error) return <p className="text-sm text-red-500">{audience.error}</p>;

  if (!audience.isTenant) return <PartnerApprovals />;

  const readOnly = !audience.canManage;

  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Approvals</p>
        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
          Everything waiting on a second pair of eyes: markets to approve, results to confirm, and recurring series your partners or teammates
          proposed. You cannot approve a market you created or confirm a result you proposed yourself; a teammate has to.
        </p>
        {readOnly && <p className="mt-2 text-xs font-semibold text-amber-600">Your account can view this queue but not act on it.</p>}
      </Card>

      <Section
        title="Markets awaiting approval"
        description="Approve to schedule a market, or reject it back to draft with a reason its creator will see."
        state={pendingMarkets}
        audience={audience}
        partnerNames={partnerNames}
        onChanged={reload}
        empty="No markets are waiting for approval."
      />

      <Section
        title="Results awaiting confirmation"
        description="A proposed result needs a teammate other than its proposer to confirm it before the market can be settled."
        state={proposedResults}
        audience={audience}
        partnerNames={partnerNames}
        onChanged={reload}
        empty="No results are waiting for confirmation."
      />

      <Card extra="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-base font-semibold text-navy-700 dark:text-white">Recurring series awaiting approval</p>
          {!pendingSeries.loading && <StatusBadge label={String(pendingSeries.items.length)} tone={pendingSeries.items.length > 0 ? "amber" : "gray"} />}
        </div>
        {pendingSeries.error && <p className="mt-3 text-sm text-red-500">{pendingSeries.error}</p>}
        {pendingSeries.loading ? (
          <p className="mt-3 text-sm text-gray-500">Loading…</p>
        ) : pendingSeries.items.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No recurring series are waiting for approval.</p>
        ) : (
          <div className="mt-2 divide-y divide-gray-100 dark:divide-white/5">
            {pendingSeries.items.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">{s.name}</p>
                  <p className="text-xs text-gray-500">{s.questionTemplate}</p>
                </div>
                <Link
                  to="/portal/market-series"
                  className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-semibold text-navy-700 dark:border-white/10 dark:text-white"
                >
                  Review in Recurring Markets
                </Link>
              </div>
            ))}
          </div>
        )}
      </Card>

      <HistoryCard
        state={history}
        title="Approval history"
        description="Every approval decision on your markets and your partners' markets: the decision, who made it, when, and their notes."
      />
    </div>
  );
}
